import { useEffect, useRef, useCallback, useState } from 'react';

const FLUSH_INTERVAL_MS = 30000; // Fallback flush when SW unavailable
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Lightweight UUID v4 — used as a stable client_id for each GPS point so the
// backend can dedup retried batches. Falls back to timestamp+random when crypto
// is unavailable (very old browsers).
function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Background GPS tracker.
 *
 * Flow:
 *  1. Registers a Service Worker that owns an IndexedDB buffer.
 *  2. In the foreground, navigator.geolocation.watchPosition collects
 *     positions and posts them to the SW for buffering.
 *  3. SW flushes the buffer to POST /api/pilot/gps in batches of 200 with
 *     exponential backoff on failure; instantly flushes on `online` event.
 *  4. If the SW isn't active yet, points go into an in-memory fallback buffer
 *     that we hand off to the SW on `beforeunload` to survive page reloads.
 *  5. SW broadcasts a GPS_STATUS message with the current queued count so
 *     callers can surface an offline badge.
 */
export default function useGPSTracker() {
  const watchIdRef = useRef(null);
  const flushTimerRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const lastFixAtRef = useRef(0);          // ms of last accepted GPS fix
  const swRef = useRef(null);
  const bufferRef = useRef([]);       // fallback buffer when SW unavailable
  const [tracking, setTracking] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [error, setError] = useState(null);
  const [pointCount, setPointCount] = useState(0);
  const [queuedCount, setQueuedCount] = useState(0);     // pending offline points
  const [unauthorized, setUnauthorized] = useState(false);

  // Phase 76h watchdog: if watchPosition stops emitting fixes for >90s while
  // we're "tracking", the phone has likely paused us (call, screen-off, OEM
  // battery saver, etc.). Tear down + restart watch + force a one-shot fix.
  const STALE_THRESHOLD_MS = 90 * 1000;
  const WATCHDOG_INTERVAL_MS = 30 * 1000;
  const POLL_INTERVAL_MS = 30 * 1000;

  // ---------- SW registration + status listener ----------
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onSWMessage = (event) => {
      const { type, data } = event.data || {};
      if (type === 'GPS_STATUS') {
        if (typeof data?.queued === 'number') setQueuedCount(data.queued);
        if (typeof data?.unauthorizedUntil === 'number') {
          setUnauthorized(data.unauthorizedUntil > Date.now());
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', onSWMessage);

    navigator.serviceWorker
      .register('/gps-sw.js')
      .then(reg => {
        swRef.current = reg;
        const sw = reg.active || reg.installing || reg.waiting;
        if (sw) {
          sw.postMessage({
            type: 'GPS_INIT',
            data: { apiUrl: API_BASE, token: localStorage.getItem('token') || '' },
          });
          sw.postMessage({ type: 'GPS_STATUS_REQ' });
        }
      })
      .catch(() => { /* SW not supported or blocked – fallback used */ });

    return () => {
      navigator.serviceWorker.removeEventListener('message', onSWMessage);
    };
  }, []);

  // ---------- Persist fallback buffer to SW on unload ----------
  useEffect(() => {
    const handoff = () => {
      if (bufferRef.current.length === 0) return;
      const sw = swRef.current?.active;
      if (sw) {
        sw.postMessage({ type: 'GPS_POINTS_BULK', data: { points: bufferRef.current.splice(0) } });
      }
    };
    window.addEventListener('beforeunload', handoff);
    window.addEventListener('pagehide', handoff);
    return () => {
      window.removeEventListener('beforeunload', handoff);
      window.removeEventListener('pagehide', handoff);
    };
  }, []);

  // Phase 76h — Push a fresh token to the SW whenever the page's localStorage
  // token changes (login, refresh, role switch). This unblocks the SW from
  // any 401-paused state without waiting the 5-min UNAUTH_PAUSE.
  useEffect(() => {
    let lastToken = localStorage.getItem('token');
    const sendToken = () => {
      const tok = localStorage.getItem('token');
      if (!tok || tok === lastToken) return;
      lastToken = tok;
      const sw = swRef.current?.active;
      if (sw) sw.postMessage({ type: 'GPS_TOKEN_REFRESH', data: { token: tok } });
    };
    // Listen for storage changes (other tabs) + a periodic poll for same-tab updates.
    window.addEventListener('storage', sendToken);
    const t = setInterval(sendToken, 15 * 1000);
    return () => {
      window.removeEventListener('storage', sendToken);
      clearInterval(t);
    };
  }, []);

  // ---------- Instant flush when page regains connectivity ----------
  useEffect(() => {
    const onOnline = () => {
      const sw = swRef.current?.active;
      if (sw) sw.postMessage({ type: 'GPS_FLUSH' });
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  // ---------- Send point to SW or local buffer ----------
  const sendPoint = useCallback((position) => {
    const point = {
      client_id: uuid(),
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      speed: position.coords.speed != null
        ? Math.round(position.coords.speed * 3.6 * 10) / 10  // m/s → km/h
        : 0,
      accuracy: Math.round(position.coords.accuracy),
      timestamp: new Date().toISOString(),
    };

    setLastPos(point);
    setPointCount(c => c + 1);
    lastFixAtRef.current = Date.now();   // watchdog heartbeat — Phase 76h

    const sw = swRef.current?.active;
    if (sw) {
      sw.postMessage({ type: 'GPS_POINT', data: point });
    } else {
      bufferRef.current.push(point);
    }
  }, []);

  // ---------- Fallback flush (only used when SW never became active) ----------
  const flushBuffer = useCallback(async () => {
    const sw = swRef.current?.active;
    if (sw) {
      // SW active — hand the whole fallback buffer over instead of posting
      // directly (keeps a single source of truth for retries + dedup).
      if (bufferRef.current.length > 0) {
        sw.postMessage({ type: 'GPS_POINTS_BULK', data: { points: bufferRef.current.splice(0) } });
        sw.postMessage({ type: 'GPS_FLUSH' });
      }
      return;
    }
    const points = bufferRef.current.splice(0);
    if (points.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      // Phase 76o — also send X-App-Version on the in-page fallback. Same
      // reason as the SW: the version gate rejects plain fetch() without it.
      await fetch(`${API_BASE}/api/pilot/gps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': '2.0',
        },
        body: JSON.stringify({ points }),
      });
    } catch {
      bufferRef.current.unshift(...points);
    }
  }, []);

  // ---------- Internal: install navigator.geolocation.watchPosition ----------
  const installWatch = useCallback(() => {
    if (!navigator.geolocation) return;
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {}
      watchIdRef.current = null;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      sendPoint,
      (err) => {
        const code = err?.code === 1 ? 'Permission denied' : err?.code === 2 ? 'Position unavailable' : err?.code === 3 ? 'GPS timeout' : (err?.message || 'GPS error');
        setError(code);
      },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
  }, [sendPoint]);

  // ---------- Start tracking ----------
  const start = useCallback(() => {
    if (tracking) return;
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }

    setError(null);
    setTracking(true);
    setPointCount(0);
    lastFixAtRef.current = Date.now();   // Phase 76h watchdog grace window

    const token = localStorage.getItem('token') || '';
    const sw = swRef.current?.active;
    if (sw) {
      sw.postMessage({
        type: 'GPS_START',
        data: { apiUrl: API_BASE, token },
      });
    }

    installWatch();

    // Phase 76g — also fire an IMMEDIATE getCurrentPosition so the first
    // GPS fix arrives in seconds (watchPosition can sometimes wait minutes
    // for the first event on a cold start).
    navigator.geolocation.getCurrentPosition(
      sendPoint,
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );

    // Register Background Sync (if supported)
    if (swRef.current?.sync) {
      swRef.current.sync.register('gps-sync').catch(() => {});
    }

    // Phase 76h — Watchdog. Every 30s, if watchPosition has been silent for
    // >90s (battery saver paused us, screen sleeping, phone call, dual-SIM
    // network switch interrupting WebView), tear down + reinstall the watch
    // AND fire a one-shot getCurrentPosition. Recovery within ~120s max.
    if (watchdogTimerRef.current) clearInterval(watchdogTimerRef.current);
    watchdogTimerRef.current = setInterval(() => {
      const stale = Date.now() - lastFixAtRef.current > STALE_THRESHOLD_MS;
      if (!stale) return;
      installWatch();
      navigator.geolocation.getCurrentPosition(
        sendPoint,
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
      );
    }, WATCHDOG_INTERVAL_MS);

    // Phase 76h — Independent periodic poll. watchPosition can be silently
    // throttled by the OS; an explicit setInterval(getCurrentPosition) gives
    // us a guaranteed fix every 30s even if watchPosition has gone silent.
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendPoint,
        () => {},
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
      );
    }, POLL_INTERVAL_MS);

    // Fallback flush timer
    flushTimerRef.current = setInterval(flushBuffer, FLUSH_INTERVAL_MS);
  }, [tracking, installWatch, sendPoint, flushBuffer]);

  // Phase 76h — Foreground / online recovery. The moment the page becomes
  // visible OR connectivity is restored, force a flush + a fresh fix so the
  // gap from being backgrounded / offline closes immediately.
  useEffect(() => {
    if (!tracking) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const sw = swRef.current?.active;
      if (sw) sw.postMessage({ type: 'GPS_FLUSH' });
      installWatch();
      try {
        navigator.geolocation.getCurrentPosition(
          sendPoint,
          () => {},
          { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
        );
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [tracking, installWatch, sendPoint]);

  // ---------- Stop tracking ----------
  const stop = useCallback(async () => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null; }
    if (watchdogTimerRef.current) { clearInterval(watchdogTimerRef.current); watchdogTimerRef.current = null; }
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }

    const sw = swRef.current?.active;
    if (sw) {
      sw.postMessage({ type: 'GPS_STOP', data: {} });
    }

    await flushBuffer();
    setTracking(false);
  }, [flushBuffer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      if (watchdogTimerRef.current) clearInterval(watchdogTimerRef.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  return { tracking, start, stop, lastPos, error, pointCount, queuedCount, unauthorized };
}
